import {
    FormControl,
    FormControlLabel,
    makeStyles,
    Radio,
    RadioGroup,
    Theme,
    useMediaQuery,
    useTheme,
  } from "@material-ui/core";
  import {
    Button,
    Compliance,
    DirectionsCar,
    Groups,
    Selector,
    Typography,
    VehicleCargo,
  } from "@thingsw/pitta-design-system";
  import {
    jwtAxiosInst,
    LightColors,
    MobileLang,
    Webviewer,
  } from "@thingsw/pitta-modules";
  import clsx from "clsx";
  import React, {
    useCallback,
    useMemo,
    useState,
  } from "react";
  import SurveyContent from "../../components/survey/SurveyContent";
  import { useTranslation } from "react-i18next";
  import ArrowBack from "@material-ui/icons/ArrowBack";
  import SurveyCheckBox from "../../components/survey/SurveyCheckBox";
  import { useHistory, useLocation } from "react-router-dom";
  import Stepper from "../../components/Stepper";
  import { useMutation, useQuery } from "@tanstack/react-query";
  
  const useStyles = makeStyles((theme: Theme) => ({
    select: {
      borderRadius: 12,
      padding: "8px 24px",
    },
    placeholderTextColor: {
      color: LightColors.primary["2"],
    },
    selectPaper: {
      width: 490,
    },
    radioRoot: {
      gap: 12,
      flexWrap: "wrap",
      width: "90%",
      marginLeft: 8,
    },
    checkboxRoot: {
      width: "50%",
      paddingTop: 16,
      [theme.breakpoints.down(Webviewer.mobile)]: {
        width: "100%",
      },
      [theme.breakpoints.up(Webviewer.mobile)]: {
        "&:nth-of-type(odd)": {
          borderRight: `1px solid ${LightColors.primary["6"]}`, // 홀수 요소의 오른쪽 테두리
        },
      },
    },
    checkboxItem: {
      margin: "0 16px",
      display: "flex",
      flexDirection: "column",
      gap: 10,
      borderBottom: `1px solid ${LightColors.primary["6"]}`,
      paddingBottom: 16,
      height: 180,
    },
    stepper: {
      [theme.breakpoints.down(Webviewer.mobile)]: {
        marginBottom: 24,
      },
      [theme.breakpoints.up(Webviewer.mobile)]: {
        marginBottom: 70,
      },
    },
    papaerClass: {
      maxHeight: "30vh",
      overflowY: "auto",
      width: 458,
      overflowX: "hidden",
      borderRadius: 8,
      border: `1px solid ${LightColors.primary["6"]}`,
      maxWidth: "calc(100vw - 32px)",
    },
  }));
  
  interface SurveyData {
    list: string[];
    key: string;
    question: string;
    seq: number;
    type: string;
  }

  interface SurveyPanelProps {
    onSkip: () => void;
  }
  
  export const SurveyPanel = ({onSkip}:SurveyPanelProps) => {
    const [step, setStep] = useState(0);
    const [industry, setIndustry] = useState("");
    const [employees, setEmployees] = useState("");
    const [vehicles, setVehicles] = useState("");
    const [reasonList, setReasonList] = useState<string[]>([]);
  
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const history = useHistory();
  
    const theme = useTheme() as Theme;
    const mobile = useMediaQuery(theme.breakpoints.down(Webviewer.mobile));
  
    const location = useLocation<{ fleetaPromo?: boolean }>();
  
    const { data } = useQuery({
      queryKey: ["survey-data"],
      queryFn: async () => {
        const lang = i18n.language;
        const res = await jwtAxiosInst.get<{
          message: string;
          response: SurveyData[];
          resultCode: string;
        }>(`/survey/form?type=fleetaFirstLogin&lang=${MobileLang[lang]}`);
  
        return res.data;
      },
      refetchInterval: false,
      refetchIntervalInBackground: false,
    });
  
    const { mutate } = useMutation({
      mutationFn: async ({
        surveyData,
        answer,
      }: {
        surveyData: SurveyData;
        answer: number[];
      }) => {
        await jwtAxiosInst.post("/survey", {
          type: surveyData.type,
          key: surveyData.key,
          answer,
        });
      },
    });
  
    const postSurvey = useCallback(() => {
      if (data) {
        const answer = [];
  
        switch (step) {
          case 0:
            answer.push(Number(industry));
            break;
          case 1:
            answer.push(data.response[1].list.indexOf(employees));
            break;
          case 2:
            answer.push(data.response[2].list.indexOf(vehicles));
            break;
          case 3:
            reasonList.forEach((reason) => {
              answer.push(data.response[3].list.indexOf(reason));
            });
            break;
          default:
            break;
        }
  
        mutate({
          surveyData: data.response[step],
          answer,
        });
      }
    }, [data, employees, industry, mutate, reasonList, step, vehicles]);
  
    const handleNext = useCallback(() => {
      postSurvey();
  
      if (step < 3 && data) {
        setStep(step + 1);
        return;
      }
      const fleetaPromo = location.state?.fleetaPromo;
      history.push("/cameras", { fleetaPromo });
    }, [data, history, location.state?.fleetaPromo, postSurvey, step]);
  
    const handleBack = () => {
      if (step > 0) {
        setStep(step - 1);
        return;
      }
    };
  
    const isDisabledNext = useMemo(() => {
      switch (step) {
        case 0:
          return industry === "";
        case 1:
          return employees === "";
        case 2:
          return vehicles === "";
        case 3:
          return reasonList.length === 0;
        default:
          return false;
      }
    }, [employees, industry, reasonList.length, step, vehicles]);
  
    const contentMarkup = useMemo(() => {
      let question = "";
  
      switch (step) {
        case 0:
          const answerList = [
            {
              key: "",
              value: "",
            },
          ];
  
          if (data?.response[0]) {
            question = data?.response[0].question;
  
            data.response[0].list.forEach((item, index) => {
              answerList.push({
                key: index.toString(),
                value: item,
              });
            });
          }
  
          return (
            <>
              <Typography htmlColor={LightColors.primary["2"]} variant="H6">
                {question}
              </Typography>
              <div
                style={{
                  position: "relative",
                }}
              >
                <Selector
                  onChange={(e) => {
                    setIndustry(e.key);
                  }}
                  keyValue={industry}
                  dense
                  paperClasses={{
                    root: classes.papaerClass,
                  }}
                  select={false}
                  placeholder={t("Business Industry")}
                  outlinedInputClassName={clsx(classes.select, {
                    [classes.placeholderTextColor]: industry === "",
                  })}
                  menuItemClassName={classes.selectPaper}
                  items={answerList}
                />
              </div>
            </>
          );
  
        case 1:
          const employeesItems: string[] = [];
  
          if (data?.response[1]) {
            question = data?.response[1].question;
  
            data.response[1].list.forEach((item) => {
              employeesItems.push(item);
            });
          }
  
          return (
            <>
              <Typography htmlColor={LightColors.primary["2"]} variant="H6">
                {question}
              </Typography>
              <FormControl
                style={{
                  width: "100%",
                }}
              >
                <RadioGroup
                  className={classes.radioRoot}
                  name="employees"
                  value={employees}
                  onChange={(e) => {
                    setEmployees(e.target.value);
                  }}
                  row
                >
                  {employeesItems.map((item) => (
                    <FormControlLabel
                      key={item}
                      value={item}
                      control={
                        <Radio
                          style={{
                            color:
                              employees !== item ? LightColors.primary["4"] : "",
                          }}
                          color="primary"
                        />
                      }
                      label={item}
                      style={{
                        border: `1px solid ${
                          employees === item
                            ? LightColors.primary["1"]
                            : LightColors.primary["4"]
                        }`,
                        color:
                          employees === item
                            ? LightColors.primary["1"]
                            : LightColors.primary["4"],
                        padding: "0 16px 0 6px",
                        borderRadius: 10,
                      }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </>
          );
        case 2:
          const vehiclesItems: string[] = [];
  
          if (data?.response[2]) {
            question = data?.response[2].question;
  
            data.response[2].list.forEach((item) => {
              vehiclesItems.push(item);
            });
          }
  
          return (
            <>
              <Typography htmlColor={LightColors.primary["2"]} variant="H6">
                {question}
              </Typography>
              <FormControl>
                <RadioGroup
                  className={classes.radioRoot}
                  name="vehicles"
                  value={vehicles}
                  onChange={(e) => {
                    setVehicles(e.target.value);
                  }}
                  row
                >
                  {vehiclesItems.map((item) => (
                    <FormControlLabel
                      key={item}
                      value={item}
                      control={
                        <Radio
                          style={{
                            color:
                              vehicles !== item ? LightColors.primary["4"] : "",
                          }}
                          color="primary"
                        />
                      }
                      label={item}
                      style={{
                        border: `1px solid ${
                          vehicles === item
                            ? LightColors.primary["1"]
                            : LightColors.primary["4"]
                        }`,
                        color:
                          vehicles === item
                            ? LightColors.primary["1"]
                            : LightColors.primary["4"],
                        padding: "0 16px 0 6px",
                        borderRadius: 10,
                      }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </>
          );
        case 3:
          const drivingReasons: {
            key: string;
            value: string;
          }[] = [];
  
          const cargoReasons: {
            key: string;
            value: string;
          }[] = [];
  
          const driverReasons: {
            key: string;
            value: string;
          }[] = [];
  
          const complianceReasons: {
            key: string;
            value: string;
          }[] = [];
  
          if (data?.response[3]) {
            question = data?.response[3].question;
  
            data.response[3].list.forEach((item, index) => {
              if (index < 3) {
                drivingReasons.push({
                  key: item,
                  value: item,
                });
              }
  
              if (index >= 3 && index < 5) {
                cargoReasons.push({
                  key: item,
                  value: item,
                });
              }
  
              if (index >= 5 && index < 7) {
                driverReasons.push({
                  key: item,
                  value: item,
                });
              }
  
              if (index >= 7) {
                complianceReasons.push({
                  key: item,
                  value: item,
                });
              }
            });
          }
  
          return (
            <>
              <Typography htmlColor={LightColors.primary["2"]} variant="H6">
                {question}
              </Typography>
              <div
                style={{
                  border: `1px solid ${LightColors.primary["6"]}`,
                  borderRadius: 16,
                  width: "100%",
                  display: "flex",
                  flexWrap: "wrap",
                  position: "relative",
                }}
              >
                {/* Driving */}
                <div className={classes.checkboxRoot}>
                  <div
                    style={{
                      margin: "0 16px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                      borderBottom: `1px solid ${LightColors.primary["6"]}`,
                      paddingBottom: 16,
                      height: mobile ? "100%" : 180,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "center",
                        marginBottom: 4,
                      }}
                    >
                      <DirectionsCar />
                      <Typography variant="H6">{t("Driving")}</Typography>
                    </div>
                    {drivingReasons.map((item) => (
                      <SurveyCheckBox
                        item={item}
                        reasonList={reasonList}
                        setReasonList={setReasonList}
                        key={item.value}
                      />
                    ))}
                  </div>
                </div>
                {/* Driver */}
                <div className={classes.checkboxRoot}>
                  <div
                    style={{
                      margin: "0 16px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                      borderBottom: `1px solid ${LightColors.primary["6"]}`,
                      paddingBottom: 16,
                      height: mobile ? "100%" : 180,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "center",
                        marginBottom: 4,
                      }}
                    >
                      <Groups />
                      <Typography variant="H6">{t("Driver")}</Typography>
                    </div>
                    {driverReasons.map((item) => (
                      <SurveyCheckBox
                        item={item}
                        reasonList={reasonList}
                        setReasonList={setReasonList}
                        key={item.value}
                      />
                    ))}
                  </div>
                </div>
                {/* Cargo */}
                <div className={classes.checkboxRoot}>
                  <div
                    style={{
                      margin: "0 16px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                      paddingBottom: 16,
                      height: mobile ? "100%" : 172,
                      borderBottom: mobile
                        ? `1px solid ${LightColors.primary["6"]}`
                        : "none",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "center",
                        marginBottom: 4,
                      }}
                    >
                      <VehicleCargo />
                      <Typography variant="H6">{t("Vehicle & Cargo")}</Typography>
                    </div>
                    {cargoReasons.map((item) => (
                      <SurveyCheckBox
                        item={item}
                        reasonList={reasonList}
                        setReasonList={setReasonList}
                        key={item.value}
                      />
                    ))}
                  </div>
                </div>
                {/* Compliance */}
                <div className={classes.checkboxRoot}>
                  <div
                    style={{
                      margin: "0 16px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                      paddingBottom: 16,
                      height: mobile ? "100%" : 172,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "center",
                        marginBottom: 4,
                      }}
                    >
                      <Compliance />
                      <Typography variant="H6">{t("Compliance")}</Typography>
                    </div>
                    {complianceReasons.map((item) => (
                      <SurveyCheckBox
                        item={item}
                        reasonList={reasonList}
                        setReasonList={setReasonList}
                        key={item.value}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </>
          );
        default:
          return <></>;
      }
    }, [
      classes.checkboxRoot,
      classes.papaerClass,
      classes.placeholderTextColor,
      classes.radioRoot,
      classes.select,
      classes.selectPaper,
      data?.response,
      employees,
      industry,
      mobile,
      reasonList,
      step,
      t,
      vehicles,
    ]);
  
    return (
      <>
        <main
          style={{
            maxWidth: 490,
            margin: "0 auto",
            marginTop: 54,
            minHeight: mobile ? "calc(100vh - 110px)" : "",
            padding: "0 16px 80px 16px",
            position: "relative",
          }}
        >
          <Stepper currentStep={step} stepCount={4} className={classes.stepper} />
  
          {/* // Survey content */}
          <SurveyContent
            title={
              step === 0
                ? t("Welcome to FLEETA Webviewer.")
                : t("Let FLEETA get_")
            }
            description={t("Help improve your_")}
            content={contentMarkup}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              position: mobile ? "absolute" : "relative",
              bottom: mobile ? 24 : 0,
              right: mobile ? 16 : 0,
              width: mobile ? "calc(100vw - 32px)" : "100%",
            }}
          >
            <div>
              {step !== 0 && (
                <Button
                  variant="text"
                  style={{
                    backgroundColor: "transparent",
                    color: LightColors.primary["2"],
                  }}
                  startIcon={<ArrowBack />}
                  onClick={handleBack}
                >
                  {t("Back")}
                </Button>
              )}
            </div>
  
            <div>
              <Button
                variant="text"
                style={{
                  backgroundColor: "transparent",
                  color: LightColors.primary["2"],
                }}
                onClick={() => onSkip()}
              >
                {t("Skip")}
              </Button>
              <Button
                disabled={isDisabledNext}
                color="primary"
                onClick={handleNext}
              >
                {step !== 3 ? t("Next") : t("Done")}
              </Button>
            </div>
          </div>
        </main>
      </>
    );
  };
  